import { useState } from "react";
import {
  Box,
  SimpleGrid,
  GridItem,
  Input,
  FormLabel,
  Button,
  useToast,
  Flex,
  IconButton,
  Select,
} from "@chakra-ui/react";
import { Editor } from "@tinymce/tinymce-react";
import PageLayout from "../../components/ui/PageLayout";
import { Link } from "react-router-dom";

import {
  CategoryQuery,
  CreateCategoryMutationVariables,
  LanguageOptions,
  UpdateCategoryMutationVariables,
  useCategoriesQuery,
} from "../../generated/graphql";
import { FcList } from "react-icons/fc";
import UploadDropzone from "../../components/dropzone/UploadDropzone";
import { createSlug } from "../../utils/slug";

interface Props {
  handleCreateCategory?: (data: CreateCategoryMutationVariables) => Promise<any>;
  handleUpdateCategory?: (data: UpdateCategoryMutationVariables) => Promise<any>;
  id?: string;
  data?: CategoryQuery["category"];
  actionType: "CREATE" | "UPDATE";
}

const Form: React.FC<Props> = ({
  handleCreateCategory,
  handleUpdateCategory,
  id,
  data,
  actionType,
}) => {
  const toast = useToast();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState(data?.photos || []);
  const [parentId, setParentId] = useState(data?.parentId || "");

  const { data: categoriesData } = useCategoriesQuery({ fetchPolicy: "network-only" });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title && actionType === "CREATE") {
      return toast({
        status: "error",
        description: "Title is required",
        title: "Error",
        duration: 1000,
      });
    }

    try {
      if (actionType === "CREATE") {
        await handleCreateCategory!({
          data: {
            descriptions: {
              create: [
                {
                  title,
                  description,
                  slug: createSlug(title),
                },
              ],
            },
            photos: { set: photos },
            ...(parentId && { parent: { connect: { id: parentId } } }),
          },
        });
      }
      if (actionType === "UPDATE") {
        await handleUpdateCategory!({
          where: {
            id: id,
          },
          data: {
            photos: { set: photos },
            ...(parentId && { parent: { connect: { id: parentId } } }),
          },
        });
      }
    } catch (error) {
      console.log("hata");
    }
  };

  return (
    <PageLayout>
      <Flex
        mb={6}
        justifyContent={"space-between"}
        alignItems="center"
        bgColor={"gray.100"}
        padding={2}
        rounded="md"
      >
        <Box fontSize={"18px"} fontWeight="bold">
          {actionType === "CREATE" ? "Create" : "Update"} Category
        </Box>
        <Flex experimental_spaceX={4}>
          <IconButton
            to={`/category`}
            as={Link}
            icon={<FcList />}
            aria-label="All Items"
          />
        </Flex>
      </Flex>
      <form onSubmit={handleSubmit}>
        <Box
          width={{
            base: "100%",
            md: "50%",
          }}
        >
          <UploadDropzone isSingle={false} images={photos} setImages={setPhotos} />
          <SimpleGrid
            columns={2}
            gap={{
              base: 6,
              md: 6,
            }}
          >
            {actionType === "CREATE" && (
              <GridItem colSpan={2}>
                <FormLabel>Title</FormLabel>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Title"
                />
              </GridItem>
            )}

            {actionType === "CREATE" && (
              <GridItem colSpan={2}>
                <FormLabel>Description</FormLabel>
                <Editor
                  apiKey="4vyocpjnhdhjtx3k08o1cv89jqhz5d6ae7910ubihafl7yes"
                  initialValue={undefined}
                  outputFormat="html"
                  onEditorChange={(e) => setDescription(e)}
                  init={{
                    skin: "snow",
                    icons: "thin",
                    placeholder: "Category description...",
                    height: 300,
                    menubar: false,
                    min_height: 200,
                    max_height: 500,

                    plugins: [
                      "advlist autolink lists link image charmap print preview anchor",
                      "searchreplace visualblocks code fullscreen",
                      "insertdatetime media table paste code wordcount",
                    ],
                    toolbar:
                      "undo redo | formatselect | " +
                      "bold italic backcolor forecolor | bullist numlist outdent indent | " +
                      "removeformat | code",
                  }}
                />
              </GridItem>
            )}

            <GridItem colSpan={2}>
              <FormLabel>Parent Category</FormLabel>
              <Select
                onChange={(e) => setParentId(e.target.value)}
                value={parentId}
                placeholder="Select Parent Category"
              >
                {categoriesData &&
                  categoriesData.categories.map((category) => {
                    const description = category.descriptions.find(
                      (d) => d.language === LanguageOptions.EN
                    );
                    return (
                      <option key={category.id} value={category.id}>
                        {description?.title}
                      </option>
                    );
                  })}
              </Select>
            </GridItem>
          </SimpleGrid>
          <Button colorScheme={"green"} type="submit" mt={6}>
            Submit
          </Button>
        </Box>
      </form>
    </PageLayout>
  );
};

export default Form;
